/**
 * Macros a link may carry — `%SUB1%`, `%UTM%`, `%OUI%`. They are substituted at serve
 * time, so they are not part of the address while it is being written.
 */
const URL_MACROS = /%[A-Z0-9_]+%/gi

/**
 * The same pattern without `g`: a global regex keeps its `lastIndex` between `.test` calls,
 * and would then answer every other call wrongly.
 */
const HAS_MACRO = new RegExp(URL_MACROS.source, 'i')

/**
 * Whether a link is one the backend will accept: an absolute `http(s)` address. Use it in a
 * zod `.refine()` on any field the API calls a link, and pair it with `ENTER_LINK` so the
 * message names the missing scheme.
 *
 * **The scheme is not optional**: a bare `example.com` is refused, which is the mistake this
 * exists to catch. So is a host with no dot, which `new URL` is happy with. Macros (`%SUB1%`)
 * are ignored, since they are substituted at serve time — but only after the host: one
 * anywhere between `//` and the path (`https://%SUB1%.example.com`, `https://example.%TLD%`)
 * is refused.
 *
 * Deliberately looser than the check on the other side, and **not a second authority** — grow
 * it into one and the two will disagree.
 */
export function isHttpUrl(value: string): boolean {
  const trimmed = value.trim()
  // Checked before the macros are stripped: stripped first, most of them leave a host that
  // still parses (`sub..example.com`, `example.`).
  const authority = /^https?:\/\/([^/?#]*)/i.exec(trimmed)?.[1] ?? ''
  if (HAS_MACRO.test(authority)) return false

  const address = trimmed.replace(URL_MACROS, '')

  if (!/^https?:\/\/\S+$/i.test(address)) return false

  try {
    const { hostname } = new URL(address)

    return hostname.includes('.') && !hostname.startsWith('.')
  } catch {
    return false
  }
}

/**
 * Whether a value looks like a host — the one check for a field the API calls a domain, as
 * against `isHttpUrl` for a field that wants a whole link. Pair it with `ENTER_DOMAIN`.
 *
 * Label shape plus a dot, and nothing more: no TLD policy, because that list is not ours to
 * copy. A single label such as `abcd` is refused, since it is not a host anyone can reach.
 */
export function isHostname(value: string): boolean {
  const host = value.trim().toLowerCase()

  if (host.length < 4 || host.length > 253) return false

  return /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/.test(
    host
  )
}
